/**
 * Work Experience Section Component
 * Displays candidate's work history with inline edit capability
 * Matches Figma design from My Profile page
 */

import React, { useState, useEffect } from 'react';
import { Building2, PenSquare, Plus, Trash2, X, Check } from 'lucide-react';
import { CandidateProfile } from '../../services/candidateServiceApi';
import { useDirtyState } from '../../hooks/useDirtyState';

interface WorkExperience {
    title: string;
    company: string;
    startDate: string;
    endDate: string;
    isCurrent?: boolean;
    description?: string;
}

interface WorkExperienceSectionProps {
    profile: CandidateProfile;
    onSave?: (experiences: WorkExperience[]) => Promise<void>;
}

const WorkExperienceSection: React.FC<WorkExperienceSectionProps> = ({ profile, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editedExperiences, setEditedExperiences] = useState<WorkExperience[]>([]);

    // Get work experience from profile
    // Priority: structured field (source of truth) > legacy fallbacks
    const workExperience = profile?.parsedResume?.experience ||
                          profile?.parsedResume?.rawJson?.experience?.work ||
                          profile?.experience?.work ||
                          [];

    // Track dirty state for save button
    const { isDirty, setOriginal, reset: resetDirty } = useDirtyState(editedExperiences);

    // Initialize edited experiences when entering edit mode
    useEffect(() => {
        if (isEditing) {
            const initialExperiences = workExperience.map((exp: any) => ({
                title: exp.title || exp.role_name || '',
                company: exp.company || exp.company_name || '',
                startDate: exp.startDate || exp.start_date || '',
                endDate: exp.endDate || exp.end_date || '',
                isCurrent: exp.isCurrent || exp.is_current || false,
                description: exp.description || ''
            }));
            setEditedExperiences(initialExperiences);
            setOriginal(initialExperiences);
        } else {
            resetDirty();
        }
    }, [isEditing]);

    // Format date range
    const formatDateRange = (startDate: string, endDate: string, isCurrent?: boolean): string => {
        if (!startDate) return 'Present';

        // Parse dates and format them
        const start = new Date(startDate).getFullYear();
        const end = isCurrent || !endDate || endDate === 'Present' ? 'Present' : new Date(endDate).getFullYear();

        return `From ${start} to ${end}`;
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedExperiences([]);
    };

    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                await onSave(editedExperiences);
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to save work experience:', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleAddExperience = () => {
        setEditedExperiences([
            ...editedExperiences,
            { title: '', company: '', startDate: '', endDate: '', isCurrent: false, description: '' }
        ]);
    };

    const handleRemoveExperience = (index: number) => {
        setEditedExperiences(editedExperiences.filter((_, i) => i !== index));
    };

    const handleExperienceChange = (index: number, field: keyof WorkExperience, value: string | boolean) => {
        const updated = [...editedExperiences];
        updated[index] = { ...updated[index], [field]: value };
        setEditedExperiences(updated);
    };

    // Show message if no experience and not editing
    if (workExperience.length === 0 && !isEditing) {
        return (
            <div className="bg-white rounded-2xl p-6 w-full border border-[#EDEDED]">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Building2 size={20} className="text-[#0B111E]" />
                        <h3
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '18px',
                                lineHeight: '25px',
                                color: '#0B111E',
                            }}
                        >
                            Work Experience
                        </h3>
                    </div>
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <Plus size={20} />
                    </button>
                </div>
                <p className="text-sm text-gray-500">No work experience added yet.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 w-full font-manrope">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                    <Building2 size={20} className="text-[#0B111E]" />
                    <h3 className="text-lg font-semibold text-[#0B111E]">Work Experience</h3>
                </div>
                {!isEditing ? (
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <PenSquare size={20} />
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCancel}
                            className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors"
                            disabled={isSaving}
                        >
                            <X size={20} />
                        </button>
                        <button
                            onClick={handleSave}
                            className={`p-2 rounded-full transition-colors ${
                                isDirty ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'
                            }`}
                            disabled={isSaving || !isDirty}
                        >
                            <Check size={20} />
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <div className="space-y-4">
                    {editedExperiences.map((exp, index) => (
                        <div key={index} className="p-4 border border-[#EDEDED] rounded-lg bg-gray-50">
                            <div className="flex justify-between items-start mb-3">
                                <span className="text-sm font-medium text-gray-600">Experience {index + 1}</span>
                                <button
                                    onClick={() => handleRemoveExperience(index)}
                                    className="text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Role/Position</label>
                                    <input
                                        type="text"
                                        value={exp.title}
                                        onChange={(e) => handleExperienceChange(index, 'title', e.target.value)}
                                        placeholder="e.g. Software Engineer"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Company</label>
                                    <input
                                        type="text"
                                        value={exp.company}
                                        onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                                        placeholder="e.g. Google"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                                    <input
                                        type="month"
                                        value={exp.startDate}
                                        onChange={(e) => handleExperienceChange(index, 'startDate', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                                    <input
                                        type="month"
                                        value={exp.endDate}
                                        onChange={(e) => handleExperienceChange(index, 'endDate', e.target.value)}
                                        placeholder="Present"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                        disabled={exp.isCurrent}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={handleAddExperience}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-[#015FA6] hover:text-[#015FA6] transition-colors flex items-center justify-center gap-2"
                    >
                        <Plus size={18} />
                        Add Experience
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {workExperience.map((exp: any, index: number) => (
                        <div key={index} className="p-3 border border-[#F4F4F4] rounded-lg hover:border-[#015FA6]/30 transition-colors">
                            <h4 className="text-sm font-medium text-[#515151] mb-1">{exp.title || exp.role_name || 'Role not specified'}</h4>
                            <p className="text-sm text-[#757575]">{exp.company || exp.company_name || 'Company not specified'} _ {formatDateRange(exp.startDate || exp.start_date, exp.endDate || exp.end_date, exp.isCurrent || exp.is_current)}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default WorkExperienceSection;
